/**
 * Glacier fendu (GDD section 10): plateaus of ice over a sea, split by crevasses, and a few islets of rock in
 * the wider channels. Dangers: the ground melts under the feet, drowning. Key: fire as a terrain weapon, to
 * melt the ice under the enemy.
 */
import { Physique, nextInt } from '@invaders/sim';
import { SEA_LEVEL_FROM_BOTTOM, plainMap, type MapState } from '../state';
import { type Gen, rect, sea, settle, waves } from './common';

const { EMPTY, ROCK, ICE, GRAVEL } = Physique;

/** Depth of the sea floor in the middle of the map, and off the coasts. */
const SHALLOW = 44;
const DEEP = SEA_LEVEL_FROM_BOTTOM - 30;

export function generateGlacier(g: Gen): MapState {
  const { w, h, rng, seaY, data } = g;
  const coastL = Math.trunc((w * 9) / 100) + nextInt(rng, 13) - 6;
  const coastR = Math.trunc((w * 91) / 100) + nextInt(rng, 13) - 6;

  // The sea floor: deep off the coasts, shallower in the middle.
  const floor = waves(g, 8, 3, 4, 11, 2, 31);
  for (let x = 0; x < w; x++) {
    const c = Math.min(x - coastL, coastR - x);
    const depth = Math.min(DEEP, Math.max(SHALLOW, DEEP - Math.trunc((Math.max(0, c) * (DEEP - SHALLOW)) / 80)) + floor(x));
    rect(g, x, seaY + depth, x, h - 1, ROCK);
  }

  // Plateaus of ice standing in the sea, from their keel under the water to their top above it. Between two
  // plateaus, a crevasse open down to the water, or a wider channel with an islet of rock in it.
  const top = waves(g, 9, 4, 5, 13, 2, 41);
  let islets = 2 + nextInt(rng, 2);
  let x = coastL + 16 + nextInt(rng, 16);
  while (x < coastR - 70) {
    const x1 = Math.min(coastR - 16, x + 90 + nextInt(rng, 80));
    const height = 36 + nextInt(rng, 50);
    const keel = 16 + nextInt(rng, 12);
    for (let xx = x; xx <= x1; xx++) {
      const e = Math.min(xx - x, x1 - xx);
      const shoulder = e < 16 ? Math.trunc(((16 - e) * (16 - e)) / 10) : 0;
      const t = seaY - height - top(xx) + shoulder;
      const bottom = seaY + keel - (e < 8 ? 8 - e : 0);
      for (let y = Math.max(0, t); y < Math.min(h, bottom); y++) if (data[y * w + xx] === EMPTY) data[y * w + xx] = ICE;
    }
    // A crack from the top that does not go through: a trap under a thin crust.
    if (x1 - x > 110) {
      const cx = x + 40 + nextInt(rng, x1 - x - 80);
      const t = seaY - height - top(cx);
      const depth = 18 + nextInt(rng, 14);
      for (let k = 0; k < depth; k++) rect(g, cx - 4 + Math.trunc((k * 4) / depth), t + k, cx + 4 - Math.trunc((k * 4) / depth), t + k, EMPTY, ICE);
    }
    const remaining = Math.trunc((coastR - 70 - x1) / 150) + 1;
    if (islets > 0 && (nextInt(rng, remaining) < islets || remaining <= islets)) {
      // A channel with an islet: a dome of rock from the sea floor, its top above the water, gravel on it.
      const gap = 80 + nextInt(rng, 40);
      const cx = x1 + (gap >> 1);
      const half = (gap >> 1) - 10;
      const peak = seaY - 14 - nextInt(rng, 30);
      for (let xx = cx - half; xx <= cx + half; xx++) {
        const d = Math.abs(xx - cx);
        const t = peak + Math.trunc(((seaY + SHALLOW - peak) * d * d * d * d) / (half * half * half * half)) + (top(xx * 5) >> 2);
        rect(g, xx, t, xx, seaY + SHALLOW + 10, ROCK);
        if (t < seaY - 2) rect(g, xx, t - 3, xx, t - 1, GRAVEL);
      }
      islets--;
      x = x1 + gap;
    } else {
      x = x1 + 8 + nextInt(rng, 12);
    }
  }

  sea(g);
  settle(g);
  return plainMap('glacier');
}
